import { ax } from "@/core";
import { AddConstructionSiteModal } from "@/modals/construction-sites/add-construction-site-modal";
import { AddProjectRequestLoseReasonModal } from "@/modals/sales/add-project-request-lose-reason-modal";
import { AddProjectRequestWonModal } from "@/modals/sales/add-project-request-won-modal";
import type { ConstructionSite, ProjectRequest } from "@msuite/katana";
import { Button, HStack, ModalButtonGroup, useDisclosure } from "@msuite/picasso";
import { type FC, Fragment, useState } from "react";
import { TbArrowRightDashed, TbCheck } from "react-icons/tb";
import { useNavigate } from "react-router-dom";

/** Props Interface */
interface ProjectRequestFinishingProps {
	projectRequestId: string;
	projectRequest: ProjectRequest | undefined;
}

export const ProjectRequestFinishing: FC<ProjectRequestFinishingProps> = ({
	projectRequestId,
	projectRequest,
}) => {
	/** Hooks */
	const [resolveIsLoading, setResolveIsLoading] = useState<boolean>(false);
	const [actionInProgress, setActionInProgress] = useState<"win" | "lose" | null>(null);
	const addProjectRequestLoseReasonModal = useDisclosure();
	const addProjectRequestWinReasonModal = useDisclosure();
	const addConstructionSiteModal = useDisclosure();

	/** Hooks */
	const navigate = useNavigate();

	/** Functions */
	async function handleOnMarkAsWon() {
		try {
			setActionInProgress("win");
			addProjectRequestWinReasonModal.onOpen();
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnMarkAsLost() {
		try {
			setActionInProgress("lose");
			addProjectRequestLoseReasonModal.onOpen();
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnCreatedConstructionSite(constructionSite: ConstructionSite) {
		try {
			if (!constructionSite.id) throw new Error("Construction site has no id");
			const constructionSiteId = constructionSite.id;
			await ax.post(`/v2/project-requests/${projectRequestId}/connect`, {
				data: { constructionSiteId },
			});
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnMarkComplete() {
		setActionInProgress(null);
	}

	async function handleOnResolve() {
		try {
			setResolveIsLoading(true);
			await ax.patch(`/v2/project-requests/${projectRequestId}/reopen`);
		} catch (error) {
			console.error(error);
		} finally {
			setResolveIsLoading(false);
		}
	}

	function handleOnClickCreateConstructionSite() {
		if (projectRequest?.constructionSiteId) {
			navigate(`/construction-sites/${projectRequest.constructionSiteId}`);
		} else {
			addConstructionSiteModal.onOpen();
		}
	}

	/** Render */
	return (
		<Fragment>
			{!projectRequest?.finalResult ? (
				<HStack
					justify="flex-end"
					alignItems="flex-start"
				>
					<ModalButtonGroup>
						<Button
							colorScheme="emerald"
							onClick={handleOnMarkAsWon}
							isDisabled={actionInProgress !== null}
							isLoading={actionInProgress === "win"}
						>
							Gewonnen
						</Button>
						<Button
							colorScheme="red"
							onClick={handleOnMarkAsLost}
							isDisabled={actionInProgress !== null}
							isLoading={actionInProgress === "lose"}
						>
							Verloren
						</Button>
					</ModalButtonGroup>
				</HStack>
			) : (
				<HStack spacing={2}>
					<Button
						variant="solid"
						onClick={projectRequest.finalResult === "win" ? handleOnMarkAsWon : handleOnMarkAsLost}
						colorScheme={projectRequest.finalResult === "win" ? "emerald" : "red"}
					>
						{projectRequest.finalResult === "win" ? "Gewonnen" : "Verloren"}
					</Button>
					<Button
						colorScheme="gray"
						size="sm"
						onClick={handleOnResolve}
						isLoading={resolveIsLoading}
					>
						Wieder öffnen
					</Button>
					{projectRequest.finalResult === "win" && (
						<Button
							colorScheme={projectRequest.constructionSiteId ? "emerald" : "gray"}
							size="sm"
							onClick={handleOnClickCreateConstructionSite}
							isDisabled={actionInProgress !== null}
							isLoading={actionInProgress === "lose"}
							leftIcon={projectRequest.constructionSiteId ? <TbCheck /> : <TbArrowRightDashed />}
						>
							{projectRequest.constructionSiteId ? "Bauvorhaben angelegt" : "Bauvorhaben anlegen"}
						</Button>
					)}
				</HStack>
			)}
			{addConstructionSiteModal.isOpen && (
				<AddConstructionSiteModal
					isOpen={addConstructionSiteModal.isOpen}
					onClose={addConstructionSiteModal.onClose}
					initialValues={{
						strasse: projectRequest?.street,
						hausnummer: projectRequest?.houseNumber,
						plz: projectRequest?.zipCode,
						ort: projectRequest?.city,
						bauvorhaben: projectRequest?.projectName,
						contractSum: projectRequest?.contractSum,
						clientId: projectRequest?.clientId,
					}}
					onComplete={handleOnCreatedConstructionSite}
				/>
			)}
			{addProjectRequestLoseReasonModal.isOpen && (
				<AddProjectRequestLoseReasonModal
					isOpen={addProjectRequestLoseReasonModal.isOpen}
					onClose={addProjectRequestLoseReasonModal.onClose}
					projectRequestId={projectRequestId}
					onComplete={handleOnMarkComplete}
				/>
			)}
			{addProjectRequestWinReasonModal.isOpen && (
				<AddProjectRequestWonModal
					isOpen={addProjectRequestWinReasonModal.isOpen}
					onClose={addProjectRequestWinReasonModal.onClose}
					projectRequestId={projectRequestId}
					onComplete={handleOnMarkComplete}
				/>
			)}
		</Fragment>
	);
};
